import { ChakraProvider } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import 'bootstrap/dist/css/bootstrap.min.css'
import GlobalSpinner from 'components/GlobalSpinner'
import { AuthenticateParams } from 'configs/constants/enums'
import { HEADER_TITLE } from 'constants/index'
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics'
import { useHeap } from 'hooks/useHeap'
import { useSmartLook } from 'hooks/useSmartlook'
import { useStores } from 'hooks/useStores'
import { ITheme } from 'interfaces/theme'
import { observer, Provider as MobxProvider } from 'mobx-react'
import { Suspense, useEffect, useState } from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import { FlagsProvider } from 'react-unleash-flags'
import { rootStore } from 'stores'
import theme from 'theme'
import { primary, secondary } from 'theme/globalStyles'
import { getSubdomain } from 'utils/domain'
import AdminRoutes from './Router/AdminRoutes'
import PublicRoutes from './Router/PublicRoutes'
import SubDomainRoutes from './Router/SubDomainRoutes'
import { registerFirebaseServiceWorker } from 'utils/firebase'
import { firebaseMessagingService } from 'services/firebaseMessaging.service'
import { useServiceWorkerReady } from 'hooks/useServiceWorkerReady'
import { useFcmSwBridge } from 'hooks/useFcmSwBridge'
import { useRefetchOnFocus } from 'hooks/useRefetchOnFocus'
import { initPerformanceMonitoring } from 'utils/performance'
import 'styles/index.scss'

// Check if Unleash environment variables are properly configured
const unleashHost = import.meta.env.REACT_APP_FLAGS_CTX_HOST
const unleashToken = import.meta.env.REACT_APP_FLAGS_ACCESS_TOKEN
const unleashInstanceId = import.meta.env.REACT_APP_FLAGS_CTX_INSTANCE_ID
const unleashAppName = import.meta.env.REACT_APP_FLAGS_CTX_APP_NAME

// Only configure Unleash if required environment variables are present
const isUnleashConfigured = unleashHost && unleashToken

// Configuration for react-unleash-flags
const flagConfig = isUnleashConfigured ? {
  appName: unleashAppName || 'development',
  host: unleashHost,
  uri: '/client/features',
  instanceId: unleashInstanceId || 'default-instance',
  extraHttpHeaders: {
    Authorization: unleashToken
  }
} : undefined

// Declare fbq on the window object
declare global {
  interface Window {
    fbq: (...args: any[]) => void
  }
}

// Initialize Meta Pixel using the environment variable with delayed loading
const pixelId = import.meta.env.REACT_APP_PIXEL_ID

if (!!pixelId) {
  // Delay loading Facebook Pixel until after page load for better performance
  const initFacebookPixel = () => {
    ;(function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      }
      if (!f._fbq) f._fbq = n
      n.push = n
      n.loaded = true
      n.version = '2.0'
      n.queue = []
      t = b.createElement(e)
      t.async = true
      t.src = v
      // Add error handling for blocked scripts
      t.onerror = function() {
        console.warn('Facebook Pixel script blocked by browser or ad blocker')
      }
      s = b.getElementsByTagName(e)[0]
      s.parentNode.insertBefore(t, s)
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')

    // Wrap fbq calls in try-catch to handle cases where script is blocked
    try {
      window.fbq('init', pixelId)
      window.fbq('track', 'PageView')
    } catch (error) {
      console.warn('Facebook Pixel initialization failed:', error)
    }
  }

  // Load Facebook Pixel after page is fully loaded or after a delay
  if (document.readyState === 'complete') {
    setTimeout(initFacebookPixel, 2500) // 2.5 second delay
  } else {
    window.addEventListener('load', () => {
      setTimeout(initFacebookPixel, 2500)
    })
  }
} else {
  console.warn('Meta Pixel ID is not defined in the environment variables')
}

// Create integrations array conditionally
const sentryIntegrations = [
  Sentry.browserTracingIntegration(),
  Sentry.globalHandlersIntegration({
    onerror: false,
    onunhandledrejection: false
  })
]

// Only add Sentry Replay in development or when explicitly enabled
if (import.meta.env.DEV || import.meta.env.REACT_APP_ENABLE_SENTRY_REPLAY === 'true') {
  sentryIntegrations.push(
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false
    }) as any
  )
}

Sentry.init({
  dsn: import.meta.env.REACT_APP_SENTRY_DSN,
  integrations: sentryIntegrations,
  environment: import.meta.env.REACT_APP_FLAGS_CTX_APP_NAME || import.meta.env.MODE,
  release: import.meta.env.VITE_APP_VERSION || 'unknown',
  tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.05, // Reduced sample rate
  // Session Replay - only if replay integration is loaded
  replaysSessionSampleRate: import.meta.env.DEV ? 1.0 : 0.01, // Further reduced for production
  replaysOnErrorSampleRate: import.meta.env.DEV ? 0.5 : 0.1, // Further reduced for production
  // Rate limiting
  maxBreadcrumbs: 50,
  // Vite-specific optimizations
  beforeSend(event) {
    // Filter out development-only errors in production
    if (!import.meta.env.DEV && event.exception) {
      const error = event.exception.values?.[0]
      if (error?.value?.includes('ChunkLoadError') || 
          error?.value?.includes('Loading chunk') ||
          error?.value?.includes('Cannot read properties of undefined') ||
          error?.value?.includes('Network Error') ||
          error?.value?.includes('ERR_BLOCKED_BY_CLIENT')) {
        return null // Don't send these common errors
      }
    }
    
    // Filter out Facebook Pixel and Unleash related errors
    if (event.exception) {
      const error = event.exception.values?.[0]
      if (error?.value?.includes('fbevents') || 
          error?.value?.includes('unleash') ||
          error?.value?.includes('find') && error?.stacktrace?.frames?.some(frame => 
            frame.filename?.includes('unleash') || frame.filename?.includes('main.esm.js'))) {
        return null
      }
    }
    
    return event
  }
})

// Register Firebase Service Worker with dynamic config
registerFirebaseServiceWorker()
  .then(() => {
    console.log('Firebase Service Worker registration completed')
  })
  .catch(error => {
    console.error('Firebase Service Worker registration failed:', error)
  })

const App = observer(() => {
  const { authStore, organizationStore } = useStores()
  const currentTheme: ITheme = organizationStore?.organization?.theme ?? {}
  const [loading, setLoading] = useState(false)
  const { isReady: isSWReady, isLoading: isSWLoading } = useServiceWorkerReady()

  // Initialize FCM SW bridge and refetch on focus
  useFcmSwBridge()
  useRefetchOnFocus()

  const subDomain = getSubdomain()
  const isInSubdomain: boolean = !!subDomain && subDomain !== 'app' && subDomain !== 'admin'
  const isInAdmin = subDomain && subDomain === 'admin'
  const query = new URLSearchParams(window?.location?.search)
  const accessToken: string = query.get(AuthenticateParams.ACCESS_TOKEN) || ''
  useHeap()
  useGoogleAnalytics()
  useSmartLook()

  // Initialize performance monitoring
  useEffect(() => {
    initPerformanceMonitoring()
  }, [])

  useEffect(() => {
    document.title = HEADER_TITLE
    authStore.getMyUser()
    setLoading(true)
    // if in subdomain need to verify the domain, or else have to redirect back to main page
    if (isInSubdomain && !isInAdmin) {
      Promise.all([
        rootStore.organizationStore.getOrganizationBySubdomain(subDomain),
        rootStore.organizationStore.getPublicSSOConfigsBySubdomain(subDomain)
      ])
        .then(() => setLoading(false))
        .catch(() => {
          const rootURL = window.location.href.replace(`${subDomain}.`, '')
          window.location.href = rootURL
        })
    } else {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (accessToken) {
      authStore.setAccessToken(accessToken)
      window.location.href = window.location.origin + window.location.pathname
    }
  }, [accessToken])

  useEffect(() => {
    if (currentTheme) {
      const root = document.documentElement
      root.style.setProperty('--current-primary-color', currentTheme?.primaryColor ?? primary)
      root.style.setProperty('--current-secondary-color', currentTheme?.secondaryColor ?? secondary)
    }
  }, [currentTheme])


  useEffect(() => {
    // Start Firebase messaging service when user is logged in
    const initializeFirebase = async () => {
      if (authStore.userDetail?.id && isSWReady && !isSWLoading) {
        console.log('Starting Firebase initialization...')
        await firebaseMessagingService.startListening()
        console.log('Firebase initialization completed')
      } else if (authStore.userDetail?.id) {
        firebaseMessagingService.stopListening()
      }
    }

    initializeFirebase()
  }, [authStore.userDetail?.id, isSWReady, isSWLoading])

  // Firebase messaging is now handled by firebaseMessagingService

  return (
    <Suspense fallback={<GlobalSpinner />}>
      {loading ? (
        <GlobalSpinner />
      ) : (
        <BrowserRouter>
          <Routes>
            {isInAdmin && <Route path="*" element={<AdminRoutes />} />}
            {isInSubdomain && <Route path="*" element={<SubDomainRoutes />} />}
            {!isInAdmin && !isInSubdomain && <Route path="*" element={<PublicRoutes />} />}
            {/* Catch-all for 404 */}
            <Route path="*" element={<div>404 - Page Not Found</div>} />
          </Routes>
        </BrowserRouter>
      )}
    </Suspense>
  )
})

const AppWithProviders = () => {
  const AppContent = () => (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Something went wrong</h2>
        <p>{error?.message}</p>
        <button onClick={resetError}>Try again</button>
      </div>
    )}>
      <App />
    </Sentry.ErrorBoundary>
  )

  return (
    <ChakraProvider theme={theme} resetCSS={false}>
      <MobxProvider {...rootStore}>
        {isUnleashConfigured ? (
          <FlagsProvider config={flagConfig}>
            <AppContent />
          </FlagsProvider>
        ) : (
          <AppContent />
        )}
        <ToastContainer
          autoClose={3000}
          theme="colored"
          style={{
            zIndex: 999999
          }}
        />
      </MobxProvider>
    </ChakraProvider>
  )
}

export default AppWithProviders